import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { API, graphqlOperation } from "aws-amplify";
import Pagination from "@mui/material/Pagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Button,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Snackbar,
  Alert as MuiAlert,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { AppContext } from "../App";
import { deleteUser, updateUser } from "../graphql/mutations";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import UserRow from "./UserRow";
import SuperAdminUserCard from "./shared/SuperAdminUserCard";
import {
  DeleteConfirmationDialog,
  UserDialog,
  CreateUserDialog,
} from "./shared";

// Helper function to find duplicate emails (case insensitive)
const findDuplicateEmails = (users) => {
  const emailCount = {};
  users.forEach((user) => {
    const normalizedEmail = user.email?.toLowerCase().trim();
    if (normalizedEmail) {
      emailCount[normalizedEmail] = (emailCount[normalizedEmail] || 0) + 1;
    }
  });
  return emailCount;
};

// Helper function to count customers per provider
const getProviderCustomerCounts = (users) => {
  const providerCount = {};

  users.forEach((user) => {
    // Only count users that have a provider (createdByUserId is set and not 'deleted')
    // and are not providers themselves (regular customers)
    const providerId = user.createdByUserId;
    const isRegularUser =
      user.isProvider === false ||
      user.isProvider === null ||
      user.isProvider === undefined;

    if (providerId && providerId !== "deleted" && isRegularUser) {
      providerCount[providerId] = (providerCount[providerId] || 0) + 1;
    }
  });

  return providerCount;
};

// Main SuperAdminUsers Component
const SuperAdminUsers = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [displayedUsers, setDisplayedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState("recordOnly");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [viewMode, setViewMode] = useState("card"); // 'card' or 'row'
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const theme = useTheme();
  const { userRecord } = useContext(AppContext);

  // Pagination settings
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(200);
  const pageSizes = [200, 500, 1000];
  const totalPages = Math.ceil(allUsers.length / pageSize);
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("DESC");

  // Custom GraphQL query with extended user information and pagination
  const listUsersCustom = `
    query ListUsers(
      $limit: Int,
      $nextToken: String
    ) {
      listUsers(limit: $limit, nextToken: $nextToken) {
        items {
          id
          username
          email
          activePricelistID
          activePricelist {
            id
            name
            userId
          }
          searchesRemaining
          subscriptionActive
          subscriptionTier
          subscriptionTerm
          isProvider
          secondPricelist {
            id
            name
          }
          companyLogo
          priceOffsetType
          priceOffsetValue
          createdAt
          lastLogin
          createdByUserId
        }
        nextToken
      }
    }
  `;

  // Track pagination state
  const [nextToken, setNextToken] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Use the pageSize state variable instead of hardcoded limits
  const initialFetchLimit = pageSize;

  const fetchUsers = useCallback(
    async (limit = 200, token = null, reset = true) => {
      if (!userRecord) return { items: [], nextToken: null };

      try {
        setLoading(true);
        const response = await API.graphql(
          graphqlOperation(listUsersCustom, { limit, nextToken: token }),
        );

        const fetchedUsers = response.data.listUsers.items || [];
        const newNextToken = response.data.listUsers.nextToken || null;

        if (reset) {
          setAllUsers(fetchedUsers);
        } else {
          setAllUsers((prev) => [...prev, ...fetchedUsers]);
        }

        setNextToken(newNextToken);
        setTotalUsers(
          reset ? fetchedUsers.length : totalUsers + fetchedUsers.length,
        );

        return { success: true, items: fetchedUsers, nextToken: newNextToken };
      } catch (error) {
        console.error("Error fetching users:", error);
        return { success: false, items: [], nextToken: null };
      } finally {
        setLoading(false);
      }
    },
    [userRecord, totalUsers],
  );

  // Load more users on demand
  const loadMoreUsers = useCallback(() => {
    if (!nextToken || loading || isLoadingMore) return;

    setIsLoadingMore(true);
    fetchUsers(pageSize, nextToken, false)
      .then(({ success }) => {
        if (success && nextToken) {
          setCurrentPage(currentPage + 1);
        }
      })
      .finally(() => {
        setIsLoadingMore(false);
      });
  }, [nextToken, loading, isLoadingMore, fetchUsers, currentPage, pageSize]);

  // Sort the users client-side
  const getSortedUsers = (usersToSort) => {
    return [...usersToSort].sort((a, b) => {
      const valA = a[sortField] || "";
      const valB = b[sortField] || "";

      // Handle date fields
      if (sortField === "createdAt" && a.createdAt && b.createdAt) {
        return sortDirection === "ASC"
          ? new Date(a.createdAt) - new Date(b.createdAt)
          : new Date(b.createdAt) - new Date(a.createdAt);
      }

      // Handle string fields
      if (typeof valA === "string" && typeof valB === "string") {
        return sortDirection === "ASC"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      return 0;
    });
  };

  const resetPagination = useCallback(() => {
    setAllUsers([]);
    setNextToken(null);
    setTotalUsers(0);
    setCurrentPage(1);
    fetchUsers(pageSize, null, true);
  }, [fetchUsers, pageSize]);

  useEffect(() => {
    resetPagination();
  }, [userRecord, resetPagination]);

  useEffect(() => {
    // Apply sorting when sort field or direction changes
    const sorted = getSortedUsers(allUsers);

    // Then apply filtering
    const filtered = sorted.filter((user) => {
      // Filter by deleted status based on toggle
      if (!showDeleted && user.createdByUserId === "deleted") {
        return false;
      }
      // Search filter
      return Object.values(user).some((value) =>
        value?.toString().toLowerCase().includes(search.toLowerCase()),
      );
    });

    // Apply pagination - only paginate displayed results, not all users
    const startIndex = (currentPage - 1) * pageSize;
    const paginated = filtered.slice(startIndex, startIndex + pageSize);

    setFilteredUsers(filtered);
    setDisplayedUsers(paginated);
  }, [
    search,
    allUsers,
    showDeleted,
    sortField,
    sortDirection,
    currentPage,
    pageSize,
  ]);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleEditUser = (updatedUser) => {
    setAllUsers(
      allUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
    );
    setDisplayedUsers(
      displayedUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
    );
    setSelectedUser(updatedUser);
  };

  const handlePricelistUpdate = async (updatedUser) => {
    console.log("[SuperAdminUsers] handlePricelistUpdate:", updatedUser);
    try {
      setAllUsers(
        allUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
      );
      setDisplayedUsers(
        displayedUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
      );
    } catch (error) {
      console.error("Error updating pricelist:", error);
    }
  };

  const handleProviderToggle = async (user) => {
    console.log("[SuperAdminUsers] handleProviderToggle:", user);
    try {
      const updatedUser = { ...user, isProvider: !user.isProvider };
      // Update local state
      setAllUsers(
        allUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
      );
      setDisplayedUsers(
        displayedUsers.map((u) => (u.id === updatedUser.id ? updatedUser : u)),
      );
      // Persist to database
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: updatedUser.id,
            isProvider: updatedUser.isProvider,
          },
        }),
      );
    } catch (error) {
      console.error("Error toggling provider status:", error);
    }
  };

  const handleProviderChange = async (userId, providerId) => {
    console.log("[SuperAdminUsers] handleProviderChange:", userId, providerId);
    try {
      // Update local state
      setAllUsers(
        allUsers.map((u) =>
          u.id === userId ? { ...u, createdByUserId: providerId } : u,
        ),
      );
      setDisplayedUsers(
        displayedUsers.map((u) =>
          u.id === userId ? { ...u, createdByUserId: providerId } : u,
        ),
      );
      // Persist to database
      await API.graphql(
        graphqlOperation(updateUser, {
          input: {
            id: userId,
            createdByUserId: providerId,
          },
        }),
      );
    } catch (error) {
      console.error("Error updating provider:", error);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Reverse direction if same field clicked
      setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
    } else {
      // New field, default to ASC
      setSortField(field);
      setSortDirection("ASC");
    }
  };

  const handleViewEdit = (user) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setDeleteType("recordOnly");
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteUser = async (type) => {
    if (!selectedUser) return;

    try {
      if (type === "complete") {
        // Update user with deleted flag in DynamoDB
        await API.graphql(
          graphqlOperation(updateUser, {
            input: {
              id: selectedUser.id,
              createdByUserId: "deleted",
            },
          }),
        );
      }

      // Delete the user record from DynamoDB
      await API.graphql(
        graphqlOperation(deleteUser, { input: { id: selectedUser.id } }),
      );

      // Update local state
      const updatedUsers = allUsers.filter((u) => u.id !== selectedUser.id);
      setAllUsers(updatedUsers);
      setDisplayedUsers(displayedUsers.filter((u) => u.id !== selectedUser.id));

      handleCloseDeleteDialog();
      setSnackbar({
        open: true,
        message: `User ${selectedUser.email} deleted successfully (${
          type === "complete" ? "complete" : "record only"
        })`,
        severity: "success",
      });
      console.log(
        `User ${
          type === "complete" ? "completely" : "record only"
        } deleted successfully`,
      );
    } catch (error) {
      console.error("Error deleting user:", error);
      setSnackbar({
        open: true,
        message: `Error deleting user: ${error.message || "Unknown error"}`,
        severity: "error",
      });
    }
  };

  // Calculate duplicate emails whenever users change
  const duplicateEmails = useMemo(
    () => findDuplicateEmails(allUsers),
    [allUsers],
  );

  // Calculate customer counts per provider
  const providerCustomerCount = useMemo(
    () => getProviderCustomerCounts(allUsers),
    [allUsers],
  );

  const isProcessing = useMemo(() => {
    // Check if processing is happening: sort, filter, or pagination is in progress
    return (
      filteredUsers.length === 0 &&
      displayedUsers.length === 0 &&
      allUsers.length > 0
    );
  }, [allUsers, filteredUsers, displayedUsers]);

  const showNoUsers = useMemo(() => {
    // Show "no users" only if really no users at all
    return !loading && displayedUsers.length === 0;
  }, [loading, displayedUsers]);

  const isInitialLoad = useMemo(() => {
    // Check if this is the initial load state
    return allUsers.length === 0 && loading;
  }, [allUsers, loading]);

  if (isInitialLoad || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Loading users...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ margin: "20px" }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search by name, email, or any field..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {displayedUsers.length} of {allUsers.length} users
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <FormControlLabel
            control={
              <Button
                size="small"
                variant={showDeleted ? "contained" : "outlined"}
                color={showDeleted ? "warning" : "inherit"}
                onClick={() => setShowDeleted(!showDeleted)}
                startIcon={<PersonOffIcon />}
              >
                {showDeleted ? "Hide Deleted" : "Show Deleted"}
              </Button>
            }
            label=""
          />
          <IconButton
            onClick={() => setViewMode("card")}
            color={viewMode === "card" ? "primary" : "default"}
            size="small"
          >
            <GridViewIcon />
          </IconButton>
          <IconButton
            onClick={() => setViewMode("row")}
            color={viewMode === "row" ? "primary" : "default"}
            size="small"
          >
            <TableRowsIcon />
          </IconButton>
        </Box>
      </Box>

      {displayedUsers.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            bgcolor: "background.paper",
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography color="text.secondary">
            {search ? "No users match your search" : "No users found"}
          </Typography>
        </Box>
      ) : viewMode === "card" ? (
        displayedUsers.map((user) => (
          <SuperAdminUserCard
            key={user.id}
            user={user}
            onView={handleViewEdit}
            onEdit={handleViewEdit}
            onDelete={handleDeleteClick}
            isDuplicate={duplicateEmails[user.email?.toLowerCase().trim()] > 1}
            allUsers={allUsers}
            onProviderChange={handleProviderChange}
            onProviderToggle={handleProviderToggle}
          />
        ))
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "email"}
                    direction={sortDirection}
                    onClick={() => handleSort("email")}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "username"}
                    direction={sortDirection}
                    onClick={() => handleSort("username")}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Pricelist</TableCell>
                <TableCell>Provider Name</TableCell>
                <TableCell>Searches</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === "createdAt"}
                    direction={sortDirection}
                    onClick={() => handleSort("createdAt")}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedUsers.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isSuperAdmin={true}
                  onPricelistUpdate={handlePricelistUpdate}
                  onViewEdit={handleViewEdit}
                  onProviderToggle={(user) => handleProviderToggle(user)}
                  onDelete={(user) => handleDeleteClick(user)}
                  customerCount={
                    user.id !== "deleted" ? providerCustomerCount[user.id] : 0
                  }
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <UserDialog
        open={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        user={selectedUser}
        onEditUser={handleEditUser}
        isSuperAdmin={true}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={() => handleDeleteUser(deleteType)}
        user={selectedUser}
        deleteType={deleteType}
      />

      <CreateUserDialog
        open={createUserModalOpen}
        onClose={() => setCreateUserModalOpen(false)}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: 3,
          mb: 2,
          gap: 2,
        }}
      >
        <TextField
          select
          size="small"
          value={pageSize}
          onChange={handlePageSizeChange}
          label="Rows per page"
          sx={{ minWidth: 120 }}
        >
          {pageSizes.map((size) => (
            <MenuItem key={size} value={size}>
              {size} per page
            </MenuItem>
          ))}
        </TextField>

        <Pagination
          count={Math.ceil(filteredUsers.length / pageSize)}
          page={currentPage}
          onChange={handlePageChange}
          boundaryCount={1}
          siblingCount={1}
          showFirstButton
          showLastButton
          disabled={loading}
          color="primary"
        />

        {nextToken && (
          <Button
            variant="outlined"
            onClick={loadMoreUsers}
            startIcon={<RefreshIcon />}
            disabled={loading || isLoadingMore}
          >
            {isLoadingMore ? "Loading..." : "Load More"}
          </Button>
        )}
      </Box>

      <Fab
        color="primary"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
        }}
        onClick={() => setCreateUserModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default SuperAdminUsers;
